import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import {
  Edit3,
  Eye,
  FileText,
  Library,
  Mail,
  Sparkles,
  Tags,
  Trash2,
} from "lucide-react";
import {
  deleteEmailTemplate,
  updateEmailTemplate,
  useEmailTemplates,
  type EmailTemplateRecord,
} from "@/hooks/use-email-templates";
import { formatDateTime } from "@/lib/i18n";
import { EmailPreview } from "./campaigns/email-preview";

function formatTemplateTimestamp(value: string | null): string {
  if (!value) {
    return "System starter";
  }

  return formatDateTime(value);
}

export default function EmailTemplatesPage() {
  const { templates, isLoading, error, refetch } = useEmailTemplates();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState("");
  const [draftSubject, setDraftSubject] = useState("");
  const [draftCategory, setDraftCategory] = useState("");
  const [draftTextContent, setDraftTextContent] = useState("");
  const [draftHtmlContent, setDraftHtmlContent] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (templates.length === 0) {
      setSelectedTemplateId(null);
      return;
    }

    setSelectedTemplateId((current) => {
      if (current && templates.some((template) => template.id === current)) {
        return current;
      }

      return templates[0]?.id ?? null;
    });
  }, [templates]);

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === selectedTemplateId) ?? null,
    [selectedTemplateId, templates],
  );

  useEffect(() => {
    if (!selectedTemplate || selectedTemplate.source !== "tenant") {
      setIsEditing(false);
      setActionError(null);
      return;
    }

    setDraftName(selectedTemplate.name);
    setDraftSubject(selectedTemplate.subject);
    setDraftCategory(selectedTemplate.category);
    setDraftTextContent(selectedTemplate.textContent ?? "");
    setDraftHtmlContent(selectedTemplate.htmlContent);
  }, [selectedTemplate]);

  const canEditSelectedTemplate = selectedTemplate?.source === "tenant";

  const handleSave = async () => {
    if (!selectedTemplate || selectedTemplate.source !== "tenant") {
      return;
    }

    setIsSaving(true);
    setActionError(null);

    try {
      const updated = await updateEmailTemplate({
        templateId: selectedTemplate.id,
        name: draftName.trim(),
        subject: draftSubject.trim(),
        htmlContent: draftHtmlContent,
        textContent: draftTextContent.trim() || null,
        category: draftCategory.trim() || selectedTemplate.category,
      });
      await refetch();
      setSelectedTemplateId(updated.id);
      setIsEditing(false);
    } catch {
      setActionError("Saving the template failed.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedTemplate || selectedTemplate.source !== "tenant") {
      return;
    }

    setIsDeleting(true);
    setActionError(null);

    try {
      await deleteEmailTemplate(selectedTemplate.id);
      setSelectedTemplateId(null);
      setIsEditing(false);
      await refetch();
    } catch {
      setActionError("Deleting the template failed.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      <section className="rounded-[28px] border border-border bg-[radial-gradient(circle_at_top_left,_rgba(59,130,246,0.14),_transparent_42%),linear-gradient(135deg,rgba(15,23,42,0.04),rgba(15,23,42,0))] p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-muted-foreground">Email Templates</p>
            <h1 className="text-2xl font-bold text-foreground">Mounted template library, preview, and builder handoff.</h1>
            <p className="max-w-3xl text-sm leading-6 text-muted-foreground">
              This workspace closes the fake template page seam. It now reads the real mounted email template API, shows merge tags from stored template truth,
              previews templates through the shared client simulator, and hands new template creation into the mounted visual builder instead of a placeholder surface.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/communications/email-builder"
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90"
            >
              <Sparkles className="h-4 w-4" />
              Create in visual builder
            </Link>
            <Link
              to="/email/campaigns"
              className="inline-flex items-center gap-2 rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-accent"
            >
              <Mail className="h-4 w-4" />
              Use in campaigns
            </Link>
          </div>
        </div>
      </section>

      <section className="grid gap-4 lg:grid-cols-3">
        <article className="rounded-[22px] border border-border bg-card p-5 shadow-sm">
          <Library className="h-5 w-5 text-primary" />
          <h2 className="mt-4 text-base font-semibold text-foreground">Mounted library</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            System starters and tenant-saved templates come from the mounted `/email-marketing/templates` contract, not hardcoded cards.
          </p>
        </article>
        <article className="rounded-[22px] border border-border bg-card p-5 shadow-sm">
          <Tags className="h-5 w-5 text-primary" />
          <h2 className="mt-4 text-base font-semibold text-foreground">Merge tags</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            Liquid variables are extracted and stored on save, then shown here so operators can verify personalization inputs before sending.
          </p>
        </article>
        <article className="rounded-[22px] border border-border bg-card p-5 shadow-sm">
          <Eye className="h-5 w-5 text-primary" />
          <h2 className="mt-4 text-base font-semibold text-foreground">Preview truth</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            Preview uses the shared client simulator with mobile-width clients and rendering quirks, instead of a fake desktop-only mock.
          </p>
        </article>
      </section>

      {isLoading ? (
        <div className="rounded-[24px] border border-dashed border-border bg-card px-6 py-12 text-center text-sm text-muted-foreground">
          Loading mounted templates…
        </div>
      ) : error ? (
        <div className="rounded-[24px] border border-destructive/20 bg-destructive/5 px-6 py-4 text-sm text-destructive">
          {error}
        </div>
      ) : templates.length === 0 ? (
        <div className="rounded-[24px] border border-dashed border-border bg-card px-6 py-12 text-center">
          <FileText className="mx-auto h-8 w-8 text-muted-foreground/40" />
          <p className="mt-3 text-sm font-medium text-foreground">No templates saved yet</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Start from the visual builder to save your first reusable template into this mounted library.
          </p>
        </div>
      ) : (
        <section className="grid gap-6 xl:grid-cols-[360px_minmax(0,1fr)]">
          <aside className="rounded-[24px] border border-border bg-card p-5 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">Library</p>
                <h2 className="mt-1 text-lg font-semibold text-foreground">Saved templates</h2>
              </div>
              <span className="rounded-full border border-border px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                {templates.length} total
              </span>
            </div>

            <div className="mt-4 space-y-2">
              {templates.map((template) => {
                const isActive = template.id === selectedTemplateId;
                return (
                  <button
                    key={template.id}
                    type="button"
                    onClick={() => setSelectedTemplateId(template.id)}
                    className={`w-full rounded-2xl border px-4 py-3 text-left transition ${
                      isActive ? "border-primary bg-primary/5 shadow-sm" : "border-border bg-background hover:border-foreground/20"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-medium text-foreground">{template.name}</div>
                        <div className="mt-1 text-xs text-muted-foreground">{template.category}</div>
                      </div>
                      <span className="rounded-full border border-border px-2 py-1 text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                        {template.source === "system" ? "System" : "Saved"}
                      </span>
                    </div>
                    <p className="mt-3 line-clamp-2 text-xs text-muted-foreground">{template.subject}</p>
                  </button>
                );
              })}
            </div>
          </aside>

          {selectedTemplate ? (
            <div className="space-y-6">
              <section className="rounded-[24px] border border-border bg-card p-6 shadow-sm">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full border border-border px-2 py-1 text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                        {selectedTemplate.source === "system" ? "System starter" : "Tenant saved"}
                      </span>
                      <span className="rounded-full border border-border px-2 py-1 text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                        {selectedTemplate.category}
                      </span>
                    </div>
                    <h2 className="text-xl font-semibold text-foreground">{selectedTemplate.name}</h2>
                    <p className="text-sm text-muted-foreground">{selectedTemplate.description}</p>
                    <div className="text-xs text-muted-foreground">
                      Last updated: {formatTemplateTimestamp(selectedTemplate.updatedAt)}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {canEditSelectedTemplate ? (
                      <>
                        <button
                          type="button"
                          onClick={() => setIsEditing((current) => !current)}
                          className="inline-flex items-center gap-2 rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-accent"
                        >
                          <Edit3 className="h-4 w-4" />
                          {isEditing ? "Close editor" : "Edit template"}
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleDelete()}
                          disabled={isDeleting}
                          className="inline-flex items-center gap-2 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-2.5 text-sm font-medium text-destructive transition hover:bg-destructive/10 disabled:opacity-50"
                        >
                          <Trash2 className="h-4 w-4" />
                          {isDeleting ? "Deleting..." : "Delete template"}
                        </button>
                      </>
                    ) : (
                      <div className="rounded-2xl border border-border bg-background px-4 py-3">
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">System starter</p>
                        <p className="mt-2 text-sm text-muted-foreground">System templates stay immutable on this seam.</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-6">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Merge tags</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {selectedTemplate.liquidVariables.length > 0 ? (
                      selectedTemplate.liquidVariables.map((variable) => (
                        <span
                          key={variable}
                          className="rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground"
                        >
                          {`{{${variable}}}`}
                        </span>
                      ))
                    ) : (
                      <span className="text-sm text-muted-foreground">No merge tags detected in this template.</span>
                    )}
                  </div>
                </div>

                {actionError ? (
                  <div className="mt-6 rounded-2xl border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                    {actionError}
                  </div>
                ) : null}

                {isEditing && canEditSelectedTemplate ? (
                  <div className="mt-6 space-y-4 rounded-[24px] border border-border bg-background p-5">
                    <div className="grid gap-4 md:grid-cols-2">
                      <label className="space-y-2 text-sm">
                        <span className="font-medium text-foreground">Template name</span>
                        <input
                          value={draftName}
                          onChange={(event) => setDraftName(event.target.value)}
                          className="w-full rounded-xl border border-border bg-card px-3 py-2 text-sm text-foreground"
                        />
                      </label>
                      <label className="space-y-2 text-sm">
                        <span className="font-medium text-foreground">Category</span>
                        <input
                          value={draftCategory}
                          onChange={(event) => setDraftCategory(event.target.value)}
                          className="w-full rounded-xl border border-border bg-card px-3 py-2 text-sm text-foreground"
                        />
                      </label>
                    </div>
                    <label className="space-y-2 text-sm">
                      <span className="font-medium text-foreground">Subject</span>
                      <input
                        value={draftSubject}
                        onChange={(event) => setDraftSubject(event.target.value)}
                        className="w-full rounded-xl border border-border bg-card px-3 py-2 text-sm text-foreground"
                      />
                    </label>
                    <label className="space-y-2 text-sm">
                      <span className="font-medium text-foreground">Text content</span>
                      <textarea
                        value={draftTextContent}
                        onChange={(event) => setDraftTextContent(event.target.value)}
                        rows={5}
                        className="w-full rounded-xl border border-border bg-card px-3 py-2 text-sm text-foreground"
                      />
                    </label>
                    <label className="space-y-2 text-sm">
                      <span className="font-medium text-foreground">HTML content</span>
                      <textarea
                        value={draftHtmlContent}
                        onChange={(event) => setDraftHtmlContent(event.target.value)}
                        rows={10}
                        className="w-full rounded-xl border border-border bg-card px-3 py-2 font-mono text-sm text-foreground"
                      />
                    </label>
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => void handleSave()}
                        disabled={isSaving || !draftName.trim() || !draftSubject.trim() || !draftHtmlContent.trim()}
                        className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-50"
                      >
                        {isSaving ? "Saving..." : "Save changes"}
                      </button>
                    </div>
                  </div>
                ) : null}
              </section>

              <section className="rounded-[24px] border border-border bg-card p-6 shadow-sm">
                <EmailPreview
                  html={isEditing && canEditSelectedTemplate ? draftHtmlContent : selectedTemplate.htmlContent}
                  title={`${selectedTemplate.name} preview`}
                />
              </section>
            </div>
          ) : null}
        </section>
      )}
    </div>
  );
}
